import { googleRules } from "./googleRules";
import { recipes } from "./recipes";
import { vocabulary } from "./vocabulary";
import type { GoogleRule, Recipe, VocabularyEntry } from "./types";

export interface CatalogType {
  name: string;
  entry: VocabularyEntry;
  rule?: GoogleRule;
}

export interface CatalogProperty {
  /** Caminho da propriedade. Pontuado quando aninha: `offers.price`. */
  path: string;
  /** Entidade que declara a propriedade — a mais específica da cadeia. */
  owner: string;
  recipe: Recipe;
}

/** Entidades que o racer pode selecionar. As aninhadas ficam de fora. */
export function selectableTypes(): CatalogType[] {
  return Object.entries(vocabulary)
    .filter(([, entry]) => !entry.nestedOnly)
    .map(([name, entry]) => ({ name, entry, rule: googleRules[name] }));
}

/**
 * A entidade e seus ancestrais, da mais específica para a mais genérica. Nenhuma
 * entidade selecionável tem ancestral hoje, mas `subClassOf` continua sendo a
 * forma como o schema.org descreve o vocabulário — e esta camada espelha o
 * schema.org, não a seleção do admin.
 */
export function resolveTypeChain(type: string): string[] {
  const chain: string[] = [];
  const pending = [type];

  while (pending.length > 0) {
    const current = pending.shift() as string;
    if (chain.includes(current)) continue;

    const entry = vocabulary[current];
    if (!entry) continue;

    chain.push(current);
    pending.push(...(entry.subClassOf ?? []));
  }

  return chain;
}

/**
 * Toda propriedade das entidades selecionadas e dos ancestrais
 * delas, sem repetir. Quando duas entidades declaram a mesma propriedade, vence
 * a primeira que aparece — as receitas são as mesmas, só o `owner` difere.
 */
export function propertiesForTypes(types: readonly string[]): CatalogProperty[] {
  const seen = new Set<string>();
  const properties: CatalogProperty[] = [];

  types.forEach((type) => {
    resolveTypeChain(type).forEach((owner) => {
      (vocabulary[owner]?.properties ?? []).forEach((path) => {
        if (seen.has(path)) return;

        const recipe = recipes[path];
        if (!recipe) return;

        seen.add(path);
        properties.push({ path, owner, recipe });
      });
    });
  });

  return properties;
}

export function recipeFor(path: string): Recipe | undefined {
  return recipes[path];
}

function collectRule(
  types: readonly string[],
  pick: (rule: GoogleRule) => readonly string[] | undefined,
): string[] {
  const collected = new Set<string>();

  types.forEach((type) => {
    resolveTypeChain(type).forEach((name) => {
      const rule = googleRules[name];
      if (!rule) return;

      (pick(rule) ?? []).forEach((property) => collected.add(property));
    });
  });

  return Array.from(collected);
}

/** Todas as propriedades obrigatórias para resultado rico das entidades escolhidas. */
export function richResultProperties(types: readonly string[]): string[] {
  return collectRule(types, (rule) => rule.requiredForRichResult);
}

/**
 * Propriedades obrigatórias para resultado rico que ficaram sem mapeamento — o
 * aviso da AC 6. Não impede o salvamento nem a emissão.
 */
export function missingRichResultProperties({
  types,
  mappedPaths,
}: {
  types: readonly string[];
  mappedPaths: readonly string[];
}): string[] {
  const mapped = new Set(mappedPaths);

  return richResultProperties(types).filter((property) => !mapped.has(property));
}

export function notesFor(types: readonly string[]): string[] {
  return collectRule(types, (rule) => (rule.note ? [rule.note] : undefined));
}

/** Todas as propriedades recomendadas para resultado rico das entidades escolhidas. */
export function recommendedRichResultProperties(
  types: readonly string[],
): string[] {
  return collectRule(types, (rule) => rule.recommendedForRichResult);
}

/**
 * Propriedades recomendadas que ficaram sem mapeamento. Vira sugestão no admin,
 * num aviso separado do das obrigatórias — recomendada que falta não impede o
 * resultado rico, e cobrá-la com o mesmo peso faria o racer perseguir o que é
 * opcional achando que é bloqueio.
 */
export function missingRecommendedRichResultProperties({
  types,
  mappedPaths,
}: {
  types: readonly string[];
  mappedPaths: readonly string[];
}): string[] {
  const mapped = new Set(mappedPaths);
  const required = new Set(richResultProperties(types));

  return recommendedRichResultProperties(types).filter(
    (property) => !mapped.has(property) && !required.has(property),
  );
}

/**
 * Mínimo para o nó existir — a AC 17. Um registro que não atende é descartado
 * na emissão, silenciosamente: não é erro de configuração, é registro sem dado.
 */
export function missingNodeProperties({
  types,
  presentPaths,
}: {
  types: readonly string[];
  presentPaths: readonly string[];
}): string[] {
  const present = new Set(presentPaths);

  return collectRule(types, (rule) => rule.requiredForNode).filter(
    (property) => !present.has(property),
  );
}
